import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import { finalize, Observable, of, Subject, switchMap } from 'rxjs';
import { EntityAuditCompareDTO, EntityChangeDTO, WrapperPageableResponseEntityChangeDTO, WrapperResponseEntityAuditCompareDTO } from '../../../core/models';
import { EntityAuditService } from '../../../services/entity-audit.service';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';

@Component({
  selector: 'app-entity-audit-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ButtonModule, TableModule, TagModule, DrawerModule, TooltipModule, JsonViewerComponent],
  templateUrl: './entity-audit-panel.component.html',
  styles: [
    `
      :host ::ng-deep .audit-json-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .audit-json-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ]
})
export class EntityAuditPanelComponent {
  entityType = input.required<string>();
  entityId = input<number | null>(null);
  entityLabel = input('registro');

  private entityAuditService = inject(EntityAuditService);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  readonly pageSizeOptions = [10, 20, 50];
  changes = signal<EntityChangeDTO[]>([]);
  loading = signal(false);
  loaded = signal(false);
  totalItems = signal(0);
  page = signal(0);
  pageSize = signal(10);
  selectedChange = signal<EntityChangeDTO | null>(null);
  drawerOpen = signal(false);
  compare = signal<EntityAuditCompareDTO | null>(null);
  compareLoading = signal(false);
  entityTimeline = signal<EntityChangeDTO[]>([]);
  timelineIndex = signal(-1);

  canGoOlderVersion = computed(() => {
    const idx = this.timelineIndex();
    return idx >= 0 && idx < this.entityTimeline().length - 1;
  });
  canGoNewerVersion = computed(() => this.timelineIndex() > 0);

  private compareRequest$ = new Subject<{
    commitId: string;
    seed: EntityChangeDTO;
    reloadTimeline: boolean;
  }>();

  constructor() {
    this.compareRequest$.pipe(
      switchMap((req): Observable<WrapperResponseEntityAuditCompareDTO | null> => {
        const entityId = this.entityId();
        if (!entityId) {
          return of(null);
        }
        this.compareLoading.set(true);
        this.compare.set(null);
        const timeline$: Observable<WrapperPageableResponseEntityChangeDTO | null> = req.reloadTimeline
          ? this.entityAuditService.getChanges(this.entityType(), entityId, { page: 0, size: 200 })
          : of(null);
        return timeline$.pipe(
          switchMap((timelineResponse) => {
            if (timelineResponse) {
              const timeline = timelineResponse.ok ? (timelineResponse.body ?? []) : [];
              this.entityTimeline.set(timeline);
              const idx = Math.max(0, timeline.findIndex((item) => this.sameCommitId(item.commitId, req.commitId)));
              this.timelineIndex.set(timeline.length ? idx : -1);
              if (timeline[idx]) {
                this.selectedChange.set({ ...req.seed, ...timeline[idx] });
              }
            } else {
              const timeline = this.entityTimeline();
              const idx = timeline.findIndex((item) => this.sameCommitId(item.commitId, req.commitId));
              if (idx >= 0) {
                this.timelineIndex.set(idx);
                this.selectedChange.set({ ...req.seed, ...timeline[idx] });
              }
            }
            return this.entityAuditService.compareWithPrevious(this.entityType(), entityId, req.commitId);
          }),
          finalize(() => {
            this.compareLoading.set(false);
            this.cdr.markForCheck();
          })
        );
      }),
      takeUntilDestroyed()
    ).subscribe({
      next: (response) => {
        if (!response) return;
        if (response.ok && response.body) {
          this.compare.set(response.body);
          const current = this.selectedChange();
          if (current) {
            this.selectedChange.set({
              ...current,
              commitId: response.body.commitId ?? current.commitId,
              author: response.body.author ?? current.author,
              commitDate: response.body.commitDate ?? current.commitDate,
              operation: response.body.operation ?? current.operation,
              version: response.body.version ?? current.version
            });
          }
        } else {
          this.toastr.error(response.message ?? 'Error al comparar versiones');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.toastr.error('Error al comparar versiones');
        this.cdr.markForCheck();
      }
    });
  }

  load(): void {
    const entityId = this.entityId();
    if (!entityId) {
      this.changes.set([]);
      this.totalItems.set(0);
      return;
    }
    this.loading.set(true);
    this.entityAuditService.getChanges(this.entityType(), entityId, {
      page: this.page(),
      size: this.pageSize()
    }).pipe(
      finalize(() => {
        this.loading.set(false);
        this.loaded.set(true);
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.changes.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems ?? 0);
            this.pageSize.set(response.page.pageSize ?? this.pageSize());
            this.page.set(Math.max(0, (response.page.currentPage ?? 1) - 1));
          }
        } else {
          this.changes.set([]);
          this.totalItems.set(0);
          this.toastr.error(response.message ?? 'Error al cargar la auditoría');
        }
      },
      error: () => {
        this.changes.set([]);
        this.totalItems.set(0);
        this.toastr.error('Error al cargar la auditoría');
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows || this.pageSize();
    const page = size ? Math.floor((event.first ?? 0) / size) : 0;
    if (page === this.page() && size === this.pageSize() && (this.loaded() || this.loading())) {
      return;
    }
    this.page.set(page);
    this.pageSize.set(size);
    this.load();
  }

  openDrawer(change: EntityChangeDTO): void {
    this.selectedChange.set(change);
    this.compare.set(null);
    this.drawerOpen.set(true);
    if (!this.entityId() || !change.commitId) {
      return;
    }
    this.compareRequest$.next({
      commitId: change.commitId,
      seed: change,
      reloadTimeline: true
    });
  }

  goToPreviousVersion(): void {
    if (!this.canGoOlderVersion()) return;
    const idx = this.timelineIndex() + 1;
    const target = this.entityTimeline()[idx];
    const current = this.selectedChange();
    if (!target?.commitId || !current) return;
    this.timelineIndex.set(idx);
    this.compareRequest$.next({
      commitId: target.commitId,
      seed: { ...current, ...target },
      reloadTimeline: false
    });
  }

  goToNextVersion(): void {
    if (!this.canGoNewerVersion()) return;
    const idx = this.timelineIndex() - 1;
    const target = this.entityTimeline()[idx];
    const current = this.selectedChange();
    if (!target?.commitId || !current) return;
    this.timelineIndex.set(idx);
    this.compareRequest$.next({
      commitId: target.commitId,
      seed: { ...current, ...target },
      reloadTimeline: false
    });
  }

  onDrawerVisibleChange(visible: boolean): void {
    this.drawerOpen.set(visible);
    if (!visible) {
      this.selectedChange.set(null);
      this.compare.set(null);
      this.entityTimeline.set([]);
      this.timelineIndex.set(-1);
    }
  }

  copyJson(): void {
    const document = this.compare()?.document ?? {};
    navigator.clipboard.writeText(JSON.stringify(document, null, 2)).then(
      () => this.toastr.success('JSON copiado'),
      () => this.toastr.error('No se pudo copiar el JSON')
    );
  }

  operationLabel(operation?: string): string {
    switch (operation) {
      case 'CREATED':
        return 'Alta';
      case 'DELETED':
        return 'Baja';
      case 'UPDATED':
        return 'Cambio';
      default:
        return operation || '—';
    }
  }

  operationSeverity(operation?: string): 'success' | 'info' | 'danger' | 'secondary' {
    switch (operation) {
      case 'CREATED':
        return 'success';
      case 'DELETED':
        return 'danger';
      case 'UPDATED':
        return 'info';
      default:
        return 'secondary';
    }
  }

  private sameCommitId(a?: string, b?: string): boolean {
    if (!a || !b) return false;
    if (a === b) return true;
    const na = Number(a);
    const nb = Number(b);
    return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
  }
}
